import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiBarChart2, FiImage, FiMousePointer } from 'react-icons/fi';
import Layout from '../components/Layout';
import ErrorBoundary from '../components/ErrorBoundary';

const AdsDashboardContent = () => {
    const [stats, setStats] = useState({
        totalAds: 0,
        totalClicks: 0,
        byCategory: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/ads/stats`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );

                if (response.data.success) {
                    setStats(response.data.data);
                }
            } catch (err) {
                setError(`Failed to fetch statistics: ${err.response?.data?.error || err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6 w-full max-w-none px-8">
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    <h2 className="text-xl font-semibold">Ads Dashboard Overview</h2>
                    <Link
                        to="/ads/list"
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                    >
                        Manage Ads
                    </Link>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md flex items-center">
                        <FiAlertCircle className="mr-2 shrink-0" />
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiImage className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-blue-800">Total Ads</h3>
                            </div>
                        </div>
                        <p className="mt-4 text-3xl font-bold text-blue-700">{stats.totalAds}</p>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiMousePointer className="h-8 w-8 text-purple-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-purple-800">Total Clicks</h3>
                            </div>
                        </div>
                        <p className="mt-4 text-3xl font-bold text-purple-700">{stats.totalClicks}</p>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiBarChart2 className="h-8 w-8 text-yellow-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-yellow-800">Categories</h3>
                            </div>
                        </div>
                        <p className="mt-4 text-3xl font-bold text-yellow-700">{stats.byCategory?.length || 0}</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Ads by Category</h3>
                {(stats.byCategory || []).length === 0 ? (
                    <p className="text-gray-500 text-sm">No category data yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {stats.byCategory.map((row) => (
                                    <tr key={row.category}>
                                        <td className="px-4 py-2 text-sm text-blue-700 font-medium">{row.category}</td>
                                        <td className="px-4 py-2 text-sm text-right">{row.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const AdsDashboard = () => {
    return (
        <Layout>
            <ErrorBoundary
                fallbackTitle="Ads Dashboard Error"
                fallbackDescription="An error occurred while loading the ads dashboard. Please try again or contact support if the problem persists."
            >
                <AdsDashboardContent />
            </ErrorBoundary>
        </Layout>
    );
};

export default AdsDashboard;
